/**
 * ¿La base de ventas en uso pasó el umbral de antigüedad? Para el punto rojo de «Público» en el menú (DISENO, «Mapa»:
 * «Público (base desactualizada)»). Es del equipo: con `activo` en false no pide nada, porque la base de ventas no la
 * ve una persona de un cliente (el backend le diría 403).
 *
 * Se pide al montar, cada 10 minutos (la antigüedad cambia de un día para el otro, y otra persona del equipo puede
 * subir una versión) y en el momento en que esta pestaña guarda una versión nueva (`avisarCambioBase`). Mientras carga
 * o con error dice false: un punto que aparece sin motivo enseña a ignorarlo, como el de Configuración. Nunca lanza,
 * así `usePolling` no lo da por detenido.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { usePolling } from "../../wa-oficial/hooks/usePolling";
import { llamar } from "../../wa-oficial/components/base/comun";
import { baseVentasApi } from "../api";
import type { EstadoBase } from "../types";

export const CADA_MS_BASE_VIEJA = 10 * 60_000;

/** Lo emite quien cambia la base en uso (guardar una versión) y lo escucha el menú. */
export const EVENTO_BASE_CAMBIO = "base-ventas:cambio";

export function avisarCambioBase(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(EVENTO_BASE_CAMBIO));
}

export function useBaseVieja(activo: boolean): boolean {
  const [vieja, setVieja] = useState(false);
  // una respuesta vieja que llega tarde no pisa a la nueva, ni prende el punto después de apagarse
  const seq = useRef(0);
  const activoRef = useRef(activo);
  activoRef.current = activo;

  const cargar = useCallback(async () => {
    const mio = ++seq.current;
    const l = await llamar<EstadoBase>(() => baseVentasApi.estado());
    if (mio !== seq.current || !activoRef.current) return;
    setVieja(l.ok && !!l.data?.vieja);
  }, []);

  useEffect(() => {
    if (!activo) {
      setVieja(false);
      return;
    }
    void cargar();
    const alCambiar = () => void cargar();
    window.addEventListener(EVENTO_BASE_CAMBIO, alCambiar);
    return () => {
      window.removeEventListener(EVENTO_BASE_CAMBIO, alCambiar);
      // desmontado (o apagado), lo que esté en camino ya no es de nadie: un setState que llega tarde, con el entorno de
      // los tests ya desarmado, tira «window is not defined» desde React
      seq.current += 1;
    };
  }, [activo, cargar]);

  usePolling(cargar, activo ? CADA_MS_BASE_VIEJA : null, [activo]);

  return activo && vieja;
}
